import { normalizePreassignment } from './preassignments';
import type { TeamPreassignment, TeamPreassignmentInput } from './types';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@rudi/api/react';
import type {
  AgentToolLatencyResponse,
  ApproveAccessRequest,
  InvestigatorAccessRequest,
  VoiceWritingPolicyRead,
  VoiceWritingProbe,
  VoiceWritingProbeRequest,
  VoiceWritingOptions,
  VoiceWritingPolicy,
  AdminTeam,
  AdminTeamCreatePayload,
  AdminTeamMember,
  AdminTeamPreassignmentPayload,
  AdminTenant,
  AdminTenantRolesListResponse,
  AdminTenantsListResponse,
  AdminUsersListResponse,
  AIChatBackend,
  ClinicalTrialsPracticeStatus,
  ClinicalTrialsPracticeSubmission,
  ClinicalTrialsProfileEditRequest,
  ClinicalTrialsProfileStatus,
  ClinicalTrialsPosterStatus,
  ClinicalTrialsPosterSubmission,
  CortexClinicalTrialsResponse,
  FeatureOverrideRow,
  FeatureOverridesResponse,
  FeatureRegistryEntry,
  FeaturesListResponse,
  Feedback,
  FeedbackAutomationConfig,
  FeedbackAutomationSettingsInput,
  FeedbackAutomationTargetInput,
  FeedbackAutomationTargetPatch,
  FeedbackListParams,
  FeedbackListResponse,
  FeedbackScreenshot,
  FeedbackScreenshotList,
  FeedbackStatus,
  IdentityAiStatus,
  IdentityCandidate,
  IdentityCandidateParams,
  IdentityDecisionResult,
  IdentityEntityDetail,
  IdentityFinding,
  IdentityFindingParams,
  IdentityFindingStatus,
  IdentityLevel,
  IdentityListParams,
  IdentityOverview,
  IdentityPage,
  IdentityRedirect,
  IdentityRows,
  IdentityVerdict,
  PendingTenant,
  PendingTenantsListResponse,
  PerformanceOperationsResponse,
  PracticeAgentBundle,
  PracticeAgentDevice,
  PracticeAgentDeviceDetail,
  PracticeAgentIssuedSetupCode,
  PracticeAgentIssuedSetupLink,
  PracticeAgentJob,
  PracticeAgentJobCreate,
  PracticeAgentRelease,
  PracticeAgentSetupCode,
  PracticeAgentSetupLink,
  PracticeAgentSetupLinkCreate,
  PracticeIssuedSetup,
  PracticeSetupLinks,
  PracticeSetupSource,
  OverduePractice,
  PatientModelKey,
  PatientModelSettings,
  PatientModelSettingsUpdate,
  PracticeCodeSets,
  PrescreeningQuery,
  PrescreeningQueryStatus,
  PrescreeningRunDetail,
  PrescreeningRunSummary,
  PrescreeningVersionCreate,
  PerformanceQueryParams,
  ProviderKeyListResponse,
  ReplayListParams,
  ReplayListResponse,
  ReplayRecordingEvent,
  SignupAttemptDeletion,
  SignupAttemptsListResponse,
  SpendPageResponse,
  TenantMembersListResponse,
  TenantDeletionImpact,
  UserDeletionImpact,
  UserDeletionResult,
  PendingUserDeletion,
  UserFeaturesResponse,
  UserFeatureState,
} from './types';

// ── Performance operations ─────────────────────────────────────────

export function usePerformanceOperations(params: PerformanceQueryParams = {}) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['performance-operations', params],
    queryFn: async () =>
      (await api.get<PerformanceOperationsResponse>('/performance/v1/overview', { params })).data,
  });
}

/** Agent tool call latency, or null from a Rudi release without it. */
export function useAgentToolLatency(days: number) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['performance-agent-tools', days],
    queryFn: async () => {
      try {
        return (
          await api.get<AgentToolLatencyResponse>('/performance/v1/agent-tools', {
            params: { days },
          })
        ).data;
      } catch (error) {
        if (isMissingRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
}

// ── Tenants ─────────────────────────────────────────────────────────

export function useTenants() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await api.get<AdminTenantsListResponse>('/admin/tenants')).data,
  });
}

/** One tenant's admin read model, so the detail page works on direct
 *  navigation and refresh without loading the whole list. */
export function useTenant(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: async () => (await api.get<AdminTenant>(`/admin/tenants/${tenantId}`)).data,
    enabled: !!tenantId,
  });
}

export function useTenantMembers(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tenant-members', tenantId],
    queryFn: async () =>
      (await api.get<TenantMembersListResponse>(`/admin/tenants/${tenantId}/members`)).data,
    enabled: !!tenantId,
  });
}

export function useRenameTenant(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      (await api.patch<AdminTenant>(`/admin/tenants/${tenantId}`, { name })).data,
    onSuccess: async data => {
      qc.setQueryData(['tenant', tenantId], data);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['tenants'] }),
        qc.invalidateQueries({ queryKey: ['tenant-members', tenantId] }),
        qc.invalidateQueries({ queryKey: ['users'] }),
      ]);
    },
  });
}

export function useTenantDeletionImpact(tenantId: string, enabled: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tenant-deletion-impact', tenantId],
    queryFn: async () =>
      (await api.get<TenantDeletionImpact>(`/admin/tenants/${tenantId}/deletion-impact`)).data,
    enabled,
  });
}

/** Deletes a tenant that holds only its setup. Rudi refuses anyone but a super admin. */
export function useDeleteTenant(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.delete<TenantDeletionImpact>(`/admin/tenants/${tenantId}`)).data,
    onSuccess: async () => {
      for (const key of ['tenant', 'tenant-members', 'tenant-teams', 'tenant-deletion-impact']) {
        qc.removeQueries({ queryKey: [key, tenantId] });
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['tenants'] }),
        qc.invalidateQueries({ queryKey: ['users'] }),
      ]);
    },
  });
}

export interface UpdateTenantConfigPayload {
  allowed_email_domains?: string[];
  // Omitted leaves the Executor pin alone; an explicit null clears it so the
  // tenant follows the Platform default; an executor writes the pin.
  ai_chat_backend?: AIChatBackend | null;
  ai_chat_model?: { model: string; effort: string } | null;
}

export function useUpdateTenantConfig(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateTenantConfigPayload) =>
      (await api.put<AdminTenant>(`/admin/tenants/${tenantId}/config`, payload)).data,
    // The PUT response carries no member counts, so refetch rather than seed
    // the cache; both the detail page and the list stay consistent.
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['tenant', tenantId] }),
        qc.invalidateQueries({ queryKey: ['tenants'] }),
      ]);
    },
  });
}

export function useUpdateTenantDomains(tenantId: string) {
  const mutation = useUpdateTenantConfig(tenantId);
  return {
    ...mutation,
    mutateAsync: (allowed_email_domains: string[]) =>
      mutation.mutateAsync({ allowed_email_domains }),
  };
}

export function usePromoteMember(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (
        await api.post<TenantMembersListResponse>(`/admin/tenants/${tenantId}/promote`, {
          user_id: userId,
        })
      ).data,
    onSuccess: data => {
      qc.setQueryData(['tenant-members', tenantId], data);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useDemoteMember(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (
        await api.post<TenantMembersListResponse>(`/admin/tenants/${tenantId}/demote`, {
          user_id: userId,
        })
      ).data,
    onSuccess: data => {
      qc.setQueryData(['tenant-members', tenantId], data);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

// ── Teams (per-tenant, super-admin) ────────────────────────────────

export function useTenantTeams(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tenant-teams', tenantId],
    queryFn: async () => (await api.get<AdminTeam[]>(`/admin/tenants/${tenantId}/teams`)).data,
    enabled: !!tenantId,
    refetchInterval: 60_000,
  });
}

export function useCreateTeam(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AdminTeamCreatePayload) =>
      (await api.post<AdminTeam>(`/admin/tenants/${tenantId}/teams`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
  });
}

export function useUpdateTeam(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      teamId,
      ...data
    }: {
      teamId: string;
      name: string;
      displayName: string;
    }) => (await api.put(`/admin/tenants/${tenantId}/teams/${teamId}`, data)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
  });
}

export function useArchiveTeam(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teamId: string) =>
      (await api.post<AdminTeam>(`/admin/tenants/${tenantId}/teams/${teamId}/archive`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
  });
}

export function useUnarchiveTeam(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (teamId: string) =>
      (await api.post<AdminTeam>(`/admin/tenants/${tenantId}/teams/${teamId}/unarchive`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
  });
}

export function useTeamMembers(tenantId: string, teamId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['team-members', tenantId, teamId],
    queryFn: async () =>
      (await api.get<AdminTeamMember[]>(`/admin/tenants/${tenantId}/teams/${teamId}/members`)).data,
    enabled: !!tenantId && !!teamId,
  });
}

export function useAddTeamMember(tenantId: string, teamId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (
        await api.post<AdminTeamMember>(`/admin/tenants/${tenantId}/teams/${teamId}/members`, {
          user_id: userId,
        })
      ).data,
    onSuccess: () => {
      // Adding is a *move* (one-team-per-user): refresh every team's member
      // list and the counts, not only this team's.
      qc.invalidateQueries({ queryKey: ['team-members', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] });
    },
  });
}

export function useRemoveTeamMember(tenantId: string, teamId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (await api.delete(`/admin/tenants/${tenantId}/teams/${teamId}/members/${userId}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] });
    },
  });
}

export function useTenantRoles(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['tenant-roles', tenantId],
    queryFn: async () =>
      (await api.get<AdminTenantRolesListResponse>(`/admin/tenants/${tenantId}/roles`)).data,
    enabled: !!tenantId,
    // Older backends have no roles endpoint — render without the selector.
    retry: false,
  });
}

export function useSetMemberRole(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, roleName }: { userId: string; roleName: string }) =>
      (
        await api.put<TenantMembersListResponse>(
          `/admin/tenants/${tenantId}/members/${userId}/role`,
          { role_name: roleName }
        )
      ).data,
    onSuccess: data => {
      qc.setQueryData(['tenant-members', tenantId], data);
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

// ── Users ───────────────────────────────────────────────────────────

export function useUsers() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get<AdminUsersListResponse>('/admin/users')).data,
  });
}

/** Mark a user internal so Rudi's Product health leaves them out (Rudi ADR 0086). */
export function useSetUserInternal() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, internal }: { userId: string; internal: boolean }) =>
      (
        await api.put<{ id: string; is_internal: boolean }>(`/admin/users/${userId}/internal`, {
          internal,
        })
      ).data,
    onSuccess: result => {
      qc.setQueryData<AdminUsersListResponse>(['users'], data =>
        data
          ? {
              ...data,
              users: data.users.map(user =>
                user.id === result.id ? { ...user, is_internal: result.is_internal } : user
              ),
            }
          : data
      );
    },
  });
}

export function useUserDeletionImpact(userId: string | null, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['user-deletion-impact', userId],
    queryFn: async () =>
      (await api.get<UserDeletionImpact>(`/admin/users/${userId}/deletion-impact`)).data,
    enabled: enabled && !!userId,
  });
}

/** Cancel every pending invitation of a person's email and delete their unused account. */
export function useDeletePendingUser() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reservationId: string) =>
      (await api.delete<PendingUserDeletion>(`/admin/pending-users/${reservationId}`)).data,
    onSuccess: () => {
      for (const key of [
        'users',
        'tenant-members',
        'tenant-teams',
        'team-members',
        'tenant-access',
      ]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export function useDeleteUser() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (await api.delete<UserDeletionResult>(`/admin/users/${userId}`)).data,
    onSuccess: (_data, userId) => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['spend'] });
      for (const key of [
        'tenant',
        'tenant-members',
        'tenant-teams',
        'team-members',
        'tenant-access',
      ]) {
        void qc.invalidateQueries({ queryKey: [key] });
      }
      qc.removeQueries({ queryKey: ['user-deletion-impact', userId] });
      qc.removeQueries({ queryKey: ['user-features', userId] });
    },
  });
}

// ── Signup attempts ─────────────────────────────────────────────────

export function useSignupAttempts(reviewed: boolean | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['signup-attempts', reviewed],
    queryFn: async () => {
      const params = reviewed === null ? {} : { reviewed };
      return (await api.get<SignupAttemptsListResponse>('/admin/signup-attempts', { params })).data;
    },
  });
}

/** Delete every sign-up attempt of an email and the account a refused sign-in left. */
export function useDeleteSignupAttempt() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<SignupAttemptDeletion>(`/admin/signup-attempts/${id}`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['signup-attempts'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

// ── PI access requests ──────────────────────────────────────────────

/** Null when this Rudi release has no PI access requests. */
export function useInvestigatorAccessRequests(userId: string, status: 'pending' | 'all' = 'all') {
  const api = useApiClient();
  return useQuery({
    queryKey: ['investigator-access-requests', userId, status],
    queryFn: async () => {
      try {
        return (
          await api.get<{ requests: InvestigatorAccessRequest[] }>(
            '/admin/investigator-access-requests',
            { params: { user_id: userId, status } }
          )
        ).data.requests;
      } catch (error) {
        const code = (error as { response?: { status?: number } })?.response?.status;
        if (code === 404 || code === 405 || code === 501) return null;
        throw error;
      }
    },
    retry: false,
  });
}

function useAccessRequestDecision<T>(action: 'approve' | 'reject') {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: T }) =>
      (
        await api.post<InvestigatorAccessRequest>(
          `/admin/investigator-access-requests/${id}/${action}`,
          body
        )
      ).data,
    onSuccess: decided => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['investigator-access-requests'] });
      qc.invalidateQueries({ queryKey: ['team-members', decided.tenant_id] });
      qc.invalidateQueries({ queryKey: ['tenant-teams', decided.tenant_id] });
    },
  });
}

export function useApproveAccessRequest() {
  return useAccessRequestDecision<ApproveAccessRequest>('approve');
}

export function useRejectAccessRequest() {
  return useAccessRequestDecision<{ review_note?: string }>('reject');
}

export function useMarkSignupReviewed() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/admin/signup-attempts/${id}/mark-reviewed`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['signup-attempts'] }),
  });
}

// ── Clinical trials ─────────────────────────────────────────────────

export function useClinicalTrialsQueue(status: ClinicalTrialsPosterStatus | 'all') {
  const api = useApiClient();
  return useQuery({
    queryKey: ['clinical-trials-queue', status],
    queryFn: async () => {
      const params = status === 'all' ? {} : { status };
      return (await api.get<CortexClinicalTrialsResponse>('/cortex/clinical-trials', { params }))
        .data;
    },
  });
}

export function useUpdateClinicalTrialsSubmissionStatus() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ClinicalTrialsPosterStatus }) =>
      (
        await api.patch<ClinicalTrialsPosterSubmission>(`/cortex/clinical-trials/${id}`, {
          status,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clinical-trials-queue'] }),
  });
}

export function useUpdateClinicalTrialsProfileRequestStatus() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ClinicalTrialsProfileStatus }) =>
      (
        await api.patch<ClinicalTrialsProfileEditRequest>(
          `/cortex/clinical-trials/profile-edit-requests/${id}`,
          { status }
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clinical-trials-queue'] }),
  });
}

export function useUpdateClinicalTrialsPracticeStatus() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ClinicalTrialsPracticeStatus }) =>
      (
        await api.patch<ClinicalTrialsPracticeSubmission>(
          `/cortex/clinical-trials/practices/${id}`,
          { status }
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clinical-trials-queue'] }),
  });
}

// ── Pending tenants ─────────────────────────────────────────────────

export function usePendingTenants(claimed: boolean | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['pending-tenants', claimed],
    queryFn: async () => {
      const params = claimed === null ? {} : { claimed };
      return (await api.get<PendingTenantsListResponse>('/admin/pending-tenants', { params })).data;
    },
  });
}

export interface PendingTenantInput {
  name: string;
  slug?: string | null;
  allowed_email_domains: string[];
}

export function useCreatePendingTenant() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PendingTenantInput) =>
      (await api.post<PendingTenant>('/admin/pending-tenants', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-tenants'] }),
  });
}

export function useUpdatePendingTenant() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: PendingTenantInput & { id: string }) =>
      (await api.patch<PendingTenant>(`/admin/pending-tenants/${id}`, input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-tenants'] }),
  });
}

export function useDeletePendingTenant() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/pending-tenants/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pending-tenants'] }),
  });
}

// ── Features (registry + overrides) ─────────────────────────────────

export function useFeatures() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['features'],
    queryFn: async () => (await api.get<FeaturesListResponse>('/cortex/features')).data,
  });
}

export function useSetPlatformOverride() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) =>
      (await api.put<FeatureRegistryEntry>(`/cortex/features/${key}/platform`, { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['features'] }),
  });
}

export function useClearPlatformOverride() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => (await api.delete(`/cortex/features/${key}/platform`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['features'] }),
  });
}

export function useFeatureOverrides(key: string | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['feature-overrides', key],
    queryFn: async () =>
      (await api.get<FeatureOverridesResponse>(`/cortex/features/${key}/overrides`)).data,
    enabled: !!key,
  });
}

/** Overrides of several features at once, sharing the per-feature cache entries. */
export function useFeatureOverridesFor(keys: string[]) {
  const api = useApiClient();
  return useQueries({
    queries: keys.map(key => ({
      queryKey: ['feature-overrides', key],
      queryFn: async () =>
        (await api.get<FeatureOverridesResponse>(`/cortex/features/${key}/overrides`)).data,
    })),
  });
}

export function useUpsertFeatureOverride() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      subject_type,
      subject_id,
      enabled,
    }: {
      key: string;
      subject_type: 'tenant' | 'role' | 'user';
      subject_id: string;
      enabled: boolean;
    }) =>
      (
        await api.put<FeatureOverrideRow>(`/cortex/features/${key}/overrides`, {
          subject_type,
          subject_id,
          enabled,
        })
      ).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['features'] });
      qc.invalidateQueries({ queryKey: ['feature-overrides', vars.key] });
      qc.invalidateQueries({ queryKey: ['user-features'] });
    },
  });
}

export function useDeleteFeatureOverride() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, overrideId }: { key: string; overrideId: string }) =>
      (await api.delete(`/cortex/features/${key}/overrides/${overrideId}`)).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['features'] });
      qc.invalidateQueries({ queryKey: ['feature-overrides', vars.key] });
      qc.invalidateQueries({ queryKey: ['user-features'] });
    },
  });
}

export function useUserFeatures(userId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['user-features', userId],
    queryFn: async () =>
      (await api.get<UserFeaturesResponse>(`/cortex/users/${userId}/features`)).data,
    enabled: !!userId,
  });
}

export function useSetUserFeature() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      key,
      enabled,
    }: {
      userId: string;
      key: string;
      enabled: boolean;
    }) =>
      (await api.put<UserFeatureState>(`/cortex/users/${userId}/features/${key}`, { enabled }))
        .data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['user-features', vars.userId] });
      qc.invalidateQueries({ queryKey: ['features'] });
      qc.invalidateQueries({ queryKey: ['feature-overrides', vars.key] });
    },
  });
}

export function useClearUserFeature() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, key }: { userId: string; key: string }) =>
      (await api.delete(`/cortex/users/${userId}/features/${key}`)).data,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['user-features', vars.userId] });
      qc.invalidateQueries({ queryKey: ['features'] });
      qc.invalidateQueries({ queryKey: ['feature-overrides', vars.key] });
    },
  });
}

// ── Session replays ─────────────────────────────────────────────────

export function useReplays(params: ReplayListParams) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['replays', params],
    queryFn: async () => (await api.get<ReplayListResponse>('/cortex/replays', { params })).data,
  });
}

export function useReplayRecording(sessionId: string | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['replay-recording', sessionId],
    queryFn: async () =>
      (await api.get<ReplayRecordingEvent[]>(`/cortex/replays/${sessionId}/recording`)).data,
    enabled: !!sessionId,
    staleTime: 1000 * 60 * 10,
  });
}

// ── Spend ───────────────────────────────────────────────────────────

export function useSpend() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['spend'],
    queryFn: async () => (await api.get<SpendPageResponse>('/cortex/spend')).data,
  });
}

export function useGrantCredit() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      user_id: string;
      amount_eur: number;
      reason: string;
      scope: 'this_month' | 'permanent';
    }) => (await api.post('/cortex/spend/credits', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spend'] }),
  });
}

export function usePatchUserCap() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      cap_eur_override,
      cap_disabled,
      reason,
    }: {
      userId: string;
      cap_eur_override: number | null;
      cap_disabled: boolean;
      reason: string;
    }) =>
      (
        await api.patch(`/cortex/spend/user/${userId}/cap`, {
          cap_eur_override,
          cap_disabled,
          reason,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spend'] }),
  });
}

export function usePatchTeamCap() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      teamId,
      cap_eur_override,
      reason,
    }: {
      teamId: string;
      cap_eur_override: number | null;
      reason: string;
    }) => (await api.patch(`/cortex/spend/team/${teamId}/cap`, { cap_eur_override, reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spend'] }),
  });
}

export function usePatchTenantCap() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tenantId,
      cap_eur_override,
      cap_disabled,
      reason,
    }: {
      tenantId: string;
      cap_eur_override: number | null;
      cap_disabled: boolean;
      reason: string;
    }) =>
      (
        await api.patch(`/cortex/spend/tenant/${tenantId}/cap`, {
          cap_eur_override,
          cap_disabled,
          reason,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spend'] }),
  });
}

// ── Provider (global) API keys ──────────────────────────────────────

export function useGlobalKeys() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['global-keys'],
    queryFn: async () => (await api.get<ProviderKeyListResponse>('/api-keys/global')).data,
  });
}

export function useUpsertGlobalKey() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      provider: string;
      api_key: string;
      display_name?: string | null;
      base_url?: string | null;
    }) => (await api.post('/api-keys/global', input)).data,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['global-keys'] }),
        // Readiness and key sources depend on the keys, so refresh every policy view.
        qc.invalidateQueries({ queryKey: ['voice-writing-platform'] }),
        qc.invalidateQueries({ queryKey: ['voice-writing-policy'] }),
        qc.invalidateQueries({ queryKey: ['voice-writing-options'] }),
      ]),
  });
}

export function useDeleteGlobalKey() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (provider: string) => (await api.delete(`/api-keys/global/${provider}`)).data,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['global-keys'] }),
        qc.invalidateQueries({ queryKey: ['dictation-settings'] }),
      ]),
  });
}

// ── Feedback ────────────────────────────────────────────────────────

export function useFeedback(params: FeedbackListParams) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['feedback', params],
    queryFn: async () =>
      (
        await api.get<FeedbackListResponse>('/cortex/feedback', {
          params: { ...params, all_tenants: true },
        })
      ).data,
  });
}

export function useUpdateFeedbackStatus() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      feedbackId: string;
      status: FeedbackStatus;
      triageNote?: string;
    }) =>
      (
        await api.patch<Feedback>(`/cortex/feedback/${input.feedbackId}/status`, {
          status: input.status,
          ...(input.triageNote !== undefined ? { triageNote: input.triageNote } : {}),
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feedback'] }),
  });
}

/** The images the reporter attached in the ticket's chat. Empty on a Rudi without the route. */
export function useFeedbackScreenshots(feedbackId: string | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['feedback-screenshots', feedbackId],
    queryFn: async (): Promise<FeedbackScreenshot[]> => {
      try {
        return (await api.get<FeedbackScreenshotList>(`/cortex/feedback/${feedbackId}/screenshots`))
          .data.items;
      } catch (error) {
        if (!isRudiError(error) && isMissingRoute(error)) return [];
        throw error;
      }
    },
    enabled: !!feedbackId,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });
}

export const useFeedbackScreenshot = useFeedbackScreenshotImage;

export function useFeedbackScreenshotImage(feedbackId: string, screenshotId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['feedback-screenshot', feedbackId, screenshotId],
    queryFn: async () =>
      (
        await api.get<Blob>(
          `/cortex/feedback/${encodeURIComponent(feedbackId)}/screenshots/${encodeURIComponent(screenshotId)}`,
          { responseType: 'blob' }
        )
      ).data,
    // A stored screenshot never changes.
    staleTime: Infinity,
    // Retry a network hiccup once; a 4xx (gone, or an older Rudi) stays an error.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      return failureCount < 1 && !(status && status >= 400 && status < 500);
    },
  });
}

/** Hands a ticket to the coding agent. Needs admin:system on Rudi. */
export function useDispatchFeedbackFix() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { feedbackId: string; overridePatientData?: boolean }) =>
      (
        await api.post<Feedback>(
          `/cortex/feedback/${input.feedbackId}/dispatch`,
          input.overridePatientData ? { overridePatientData: true } : undefined
        )
      ).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ['feedback'] }),
  });
}

const FEEDBACK_AUTOMATION = '/cortex/feedback-automation';
const FEEDBACK_AUTOMATION_KEY = ['feedback-automation'];

/** True when this Rudi release has no Feedback automation settings. */
export function isMissingFeedbackAutomationRoute(error: unknown): boolean {
  return !isRudiError(error) && isMissingRoute(error);
}

/** Where this Rudi sends tickets. Null when its release can't manage that yet. */
export function useFeedbackAutomation() {
  const api = useApiClient();
  return useQuery({
    queryKey: FEEDBACK_AUTOMATION_KEY,
    queryFn: async () => {
      try {
        return (await api.get<FeedbackAutomationConfig>(FEEDBACK_AUTOMATION)).data;
      } catch (error) {
        if (isMissingFeedbackAutomationRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
}

/** Every write answers with the new config; a failed one may mean it moved on, so refetch. */
function useFeedbackAutomationMutation<V>(
  request: (api: ReturnType<typeof useApiClient>, variables: V) => Promise<{ data: unknown }>
) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (variables: V) =>
      (await request(api, variables)).data as FeedbackAutomationConfig,
    onSuccess: data => qc.setQueryData(FEEDBACK_AUTOMATION_KEY, data),
    onSettled: () => qc.invalidateQueries({ queryKey: FEEDBACK_AUTOMATION_KEY }),
  });
}

export function useSaveFeedbackAutomationSettings() {
  return useFeedbackAutomationMutation((api, body: FeedbackAutomationSettingsInput) =>
    api.put(`${FEEDBACK_AUTOMATION}/settings`, body)
  );
}

/** Back to the environment's switch, threshold and categories. */
export function useResetFeedbackAutomationSettings() {
  return useFeedbackAutomationMutation(api => api.delete(`${FEEDBACK_AUTOMATION}/settings`));
}

export function useAddFeedbackAutomationTarget() {
  return useFeedbackAutomationMutation((api, body: FeedbackAutomationTargetInput) =>
    api.post(`${FEEDBACK_AUTOMATION}/targets`, body)
  );
}

export function useUpdateFeedbackAutomationTarget() {
  return useFeedbackAutomationMutation(
    (api, input: { targetId: string; patch: FeedbackAutomationTargetPatch }) =>
      api.patch(`${FEEDBACK_AUTOMATION}/targets/${encodeURIComponent(input.targetId)}`, input.patch)
  );
}

/** `null` hands tickets back to the environment fallback. */
export function useSetActiveFeedbackAutomationTarget() {
  return useFeedbackAutomationMutation((api, targetId: string | null) =>
    api.put(`${FEEDBACK_AUTOMATION}/active`, { targetId })
  );
}

export function useDeleteFeedbackAutomationTarget() {
  return useFeedbackAutomationMutation((api, targetId: string) =>
    api.delete(`${FEEDBACK_AUTOMATION}/targets/${encodeURIComponent(targetId)}`)
  );
}

export function useVoiceWritingPolicy(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['voice-writing-policy', tenantId],
    queryFn: async () =>
      (await api.get<VoiceWritingPolicyRead>(`/cortex/tenants/${tenantId}/voice-writing`)).data,
    retry: false,
  });
}

export function useVoiceWritingOptions(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['voice-writing-options', tenantId],
    queryFn: async () =>
      (await api.get<VoiceWritingOptions>(`/cortex/tenants/${tenantId}/voice-writing/options`))
        .data,
    retry: false,
  });
}

export function useSaveVoiceWritingPolicy(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { expected_revision: number; policy: VoiceWritingPolicy }) =>
      (await api.put<VoiceWritingPolicyRead>(`/cortex/tenants/${tenantId}/voice-writing`, payload))
        .data,
    onSuccess: data => qc.setQueryData(['voice-writing-policy', tenantId], data),
  });
}

export function useResetVoiceWritingPolicy(tenantId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expectedRevision: number) =>
      (
        await api.delete<VoiceWritingPolicyRead>(`/cortex/tenants/${tenantId}/voice-writing`, {
          params: { expected_revision: expectedRevision },
        })
      ).data,
    onSuccess: data => qc.setQueryData(['voice-writing-policy', tenantId], data),
  });
}

export function usePlatformVoiceWritingPolicy() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['voice-writing-platform', 'policy'],
    queryFn: async () =>
      (await api.get<VoiceWritingPolicyRead>('/cortex/voice-writing/platform')).data,
    retry: false,
  });
}

export function usePlatformVoiceWritingOptions() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['voice-writing-platform', 'options'],
    queryFn: async () =>
      (await api.get<VoiceWritingOptions>('/cortex/voice-writing/platform/options')).data,
    retry: false,
  });
}

export function useSavePlatformVoiceWritingPolicy() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { expected_revision: number; policy: VoiceWritingPolicy }) =>
      (await api.put<VoiceWritingPolicyRead>('/cortex/voice-writing/platform', payload)).data,
    onSuccess: data =>
      Promise.all([
        qc.setQueryData(['voice-writing-platform', 'policy'], data),
        // Tenants without their own policy inherit this one.
        qc.invalidateQueries({ queryKey: ['voice-writing-policy'] }),
      ]),
  });
}

export function useRemovePlatformVoiceWritingPolicy() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (expectedRevision: number) =>
      (
        await api.delete<VoiceWritingPolicyRead>('/cortex/voice-writing/platform', {
          params: { expected_revision: expectedRevision },
        })
      ).data,
    onSuccess: data =>
      Promise.all([
        qc.setQueryData(['voice-writing-platform', 'policy'], data),
        qc.invalidateQueries({ queryKey: ['voice-writing-policy'] }),
      ]),
  });
}

export function useVoiceWritingProbe(tenantId: string) {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (payload: VoiceWritingProbeRequest) =>
      (
        await api.post<VoiceWritingProbe>(
          `/cortex/tenants/${tenantId}/voice-writing/probe`,
          payload
        )
      ).data,
  });
}

export function useVoiceWritingSources(tenantId: string, revision?: number) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['voice-writing-sources', tenantId, revision],
    queryFn: async () => {
      const { data } = await api.get<
        {
          source: 'phone' | 'video';
          schema_version: number;
          ready: boolean;
          saved_revision: number;
          worker_revision: number | null;
          issues: string[];
        }[]
      >(`/cortex/tenants/${tenantId}/voice-writing/sources`);
      if (!Array.isArray(data)) throw new Error('Unsupported source readiness');
      return data;
    },
    retry: false,
    refetchInterval: 5000,
  });
}

export function useProvisionPendingTenant() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<{ id: string }>(`/admin/pending-tenants/${id}/provision`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pending-tenants'] });
      qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}

export function useTeamPreassignments(tenantId: string, teamId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['team-preassignments', tenantId, teamId],
    queryFn: async () =>
      (
        await api.get<TeamPreassignment[]>(
          `/admin/tenants/${tenantId}/teams/${teamId}/preassignments`,
          { params: { includeHistory: true } }
        )
      ).data.map(normalizePreassignment),
    enabled: !!tenantId && !!teamId,
    retry: false,
    refetchInterval: query =>
      query.state.data?.some(row => row.status === 'pending') ? 5000 : false,
  });
}

export function useCreateTeamPreassignments(tenantId: string, teamId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (members: TeamPreassignmentInput[]) =>
      (
        await api.post<TeamPreassignment[]>(
          `/admin/tenants/${tenantId}/teams/${teamId}/preassignments`,
          { members }
        )
      ).data,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['team-preassignments', tenantId] }),
        qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
      ]),
  });
}

export function useCancelTeamPreassignment(tenantId: string, teamId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/admin/tenants/${tenantId}/teams/${teamId}/preassignments/${id}`),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['team-preassignments', tenantId] }),
        qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
      ]),
  });
}

export function useCreateLegacyTeamPreassignment(tenantId: string, teamId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (member: AdminTeamPreassignmentPayload) =>
      (await api.post(`/admin/tenants/${tenantId}/teams/${teamId}/preassignments`, member)).data,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['team-preassignments', tenantId] }),
        qc.invalidateQueries({ queryKey: ['tenant-teams', tenantId] }),
      ]),
  });
}

// ── Medical data quality ────────────────────────────────────────────
// Rudi forwards these to the Medical Repository's identity admin API and adds
// the operator as the actor, so no body here carries an actor.

const IDENTITY = '/cortex/medical-repository/identity';

/** The URL segment of each level. */
export const IDENTITY_PATHS: Record<IdentityLevel, string> = {
  organisation: 'organisations',
  site: 'catalog-sites',
  department: 'departments',
  person: 'persons',
};

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

/** True when this Rudi release has no such route. */
function isMissingRoute(error: unknown): boolean {
  const code = statusOf(error);
  return code === 404 || code === 405 || code === 501;
}

function withReason<T extends object>(body: T, reason?: string): T & { reason?: string } {
  const text = reason?.trim();
  return text ? { ...body, reason: text } : body;
}

/** Null when this Rudi release does not forward identity resolution. */
export function useIdentityOverview() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['identity', 'overview'],
    queryFn: async () => {
      try {
        return (await api.get<IdentityOverview>(`${IDENTITY}/overview`)).data;
      } catch (error) {
        if (isMissingRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
}

/** Null when this Rudi release has no AI triage. */
export function useIdentityAiStatus() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['identity', 'ai-status'],
    queryFn: async () => {
      try {
        return (await api.get<IdentityAiStatus>(`${IDENTITY}/ai/status`)).data;
      } catch (error) {
        if (isMissingRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
}

export function useIdentityEntities<L extends IdentityLevel>(
  level: L,
  params: IdentityListParams,
  enabled = true
) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['identity', 'list', level, params],
    queryFn: async () =>
      (
        await api.get<IdentityPage<IdentityRows[L]>>(`${IDENTITY}/${IDENTITY_PATHS[level]}`, {
          params,
        })
      ).data,
    enabled,
    retry: false,
  });
}

export function useIdentityEntity(level: IdentityLevel, id: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['identity', 'entity', level, id],
    queryFn: async () =>
      (
        await api.get<IdentityEntityDetail | IdentityRedirect>(
          `${IDENTITY}/${IDENTITY_PATHS[level]}/${id}`
        )
      ).data,
    enabled: !!id,
    retry: false,
  });
}

export function useLinkCandidates(params: IdentityCandidateParams) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['identity', 'candidates', params],
    queryFn: async () =>
      (await api.get<IdentityPage<IdentityCandidate>>(`${IDENTITY}/link-candidates`, { params }))
        .data,
    retry: false,
  });
}

export function useIdentityFindings(params: IdentityFindingParams) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['identity', 'findings', params],
    queryFn: async () =>
      (await api.get<IdentityPage<IdentityFinding>>(`${IDENTITY}/findings`, { params })).data,
    retry: false,
  });
}

/** A decision changes other entities' trees and counts, so refresh every identity view. */
function useIdentityMutation<V, R>(
  request: (api: ReturnType<typeof useApiClient>, variables: V) => Promise<R>
) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variables: V) => request(api, variables),
    onSettled: () => qc.invalidateQueries({ queryKey: ['identity'] }),
  });
}

export function useDecideLinkCandidate() {
  return useIdentityMutation(
    async (
      api,
      { id, verdict, reason }: { id: string; verdict: IdentityVerdict; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/link-candidates/${id}/decision`,
          withReason({ verdict }, reason)
        )
      ).data
  );
}

export interface BulkDecisionOutcome {
  id: string;
  error: unknown;
}

/** Accept several Link Candidates as the same, one decision per candidate, in order. */
export function useAcceptLinkCandidates() {
  return useIdentityMutation(async (api, ids: string[]) => {
    const outcomes: BulkDecisionOutcome[] = [];
    for (const id of ids) {
      try {
        await api.post<IdentityDecisionResult>(`${IDENTITY}/link-candidates/${id}/decision`, {
          verdict: 'same',
        });
        outcomes.push({ id, error: null });
      } catch (error) {
        outcomes.push({ id, error });
      }
    }
    return outcomes;
  });
}

export function useMergeIdentity() {
  return useIdentityMutation(
    async (
      api,
      {
        level,
        sourceId,
        targetId,
        reason,
      }: { level: IdentityLevel; sourceId: string; targetId: string; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/${IDENTITY_PATHS[level]}/merge`,
          withReason({ source_id: sourceId, target_id: targetId }, reason)
        )
      ).data
  );
}

export function useSplitIdentity() {
  return useIdentityMutation(
    async (
      api,
      {
        level,
        id,
        mentionIds,
        reason,
      }: { level: IdentityLevel; id: string; mentionIds: string[]; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/${IDENTITY_PATHS[level]}/${id}/split`,
          withReason({ mention_ids: mentionIds }, reason)
        )
      ).data
  );
}

export function useNotSameIdentity() {
  return useIdentityMutation(
    async (
      api,
      {
        level,
        leftId,
        rightId,
        reason,
      }: { level: IdentityLevel; leftId: string; rightId: string; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/${IDENTITY_PATHS[level]}/not-same`,
          withReason({ left_id: leftId, right_id: rightId }, reason)
        )
      ).data
  );
}

/**
 * Link a Mention by hand, such as to the Catalog Site at its address. Every
 * Mention written the same way follows, and its open Link Candidates at that
 * level are superseded.
 */
export function useLinkMention() {
  return useIdentityMutation(
    async (
      api,
      {
        mentionId,
        level,
        entityId,
        reason,
      }: { mentionId: string; level: IdentityLevel; entityId: string; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/mentions/${mentionId}/link`,
          withReason({ level, entity_id: entityId }, reason)
        )
      ).data
  );
}

/** Move a Catalog Site to another Organisation, or a Department to another Catalog Site. */
export function useMoveIdentity() {
  return useIdentityMutation(
    async (
      api,
      {
        level,
        id,
        parentId,
        reason,
      }: { level: 'site' | 'department'; id: string; parentId: string; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/${IDENTITY_PATHS[level]}/${id}/move`,
          withReason(
            level === 'site' ? { organisation_id: parentId } : { site_id: parentId },
            reason
          )
        )
      ).data
  );
}

export function useSetOrganisationParent() {
  return useIdentityMutation(
    async (
      api,
      { id, parentId, reason }: { id: string; parentId: string | null; reason?: string }
    ) =>
      (
        await api.post<IdentityDecisionResult>(
          `${IDENTITY}/organisations/${id}/parent`,
          withReason({ parent_id: parentId }, reason)
        )
      ).data
  );
}

export interface IdentityEditInput {
  level: IdentityLevel;
  id: string;
  displayName?: string;
  addAliases?: string[];
  removeAliases?: string[];
  distinctive?: boolean;
  reason?: string;
}

/** Rename an entity, or add and remove aliases of an Organisation or Catalog Site. */
export function useEditIdentity() {
  return useIdentityMutation(
    async (
      api,
      { level, id, displayName, addAliases, removeAliases, distinctive, reason }: IdentityEditInput
    ) =>
      (
        await api.patch<IdentityDecisionResult[]>(
          `${IDENTITY}/${IDENTITY_PATHS[level]}/${id}`,
          withReason(
            {
              ...(displayName !== undefined ? { display_name: displayName } : {}),
              add_aliases: addAliases ?? [],
              remove_aliases: removeAliases ?? [],
              distinctive: distinctive ?? false,
            },
            reason
          )
        )
      ).data
  );
}

export function useFlagIdentity() {
  return useIdentityMutation(
    async (api, { level, id, note }: { level: IdentityLevel; id: string; note: string }) =>
      (
        await api.post<IdentityFinding>(`${IDENTITY}/${IDENTITY_PATHS[level]}/${id}/flags`, {
          note,
        })
      ).data
  );
}

export function useSetFindingStatus() {
  return useIdentityMutation(
    async (
      api,
      { id, status, note }: { id: string; status: IdentityFindingStatus; note?: string }
    ) =>
      (
        await api.post<IdentityFinding>(`${IDENTITY}/findings/${id}/status`, {
          status,
          ...(note?.trim() ? { note: note.trim() } : {}),
        })
      ).data
  );
}

export function useStartIdentityTriage() {
  return useIdentityMutation(
    async (api, limit: number) =>
      (await api.post<{ queued: number }>(`${IDENTITY}/ai/triage`, { limit })).data
  );
}

// ── Practice computers ──────────────────────────────────────────────
// Rudi's `/cortex/practice-agents` routes. A Rudi without them answers 404. A
// Rudi with the device routes but without a newer static path, such as
// `/releases`, reads the path as a device ID and answers 422 or 405.

const PRACTICE_AGENTS = '/cortex/practice-agents';

/** Rudi's own errors carry `error`; a route FastAPI does not know carries `detail`. */
function isRudiError(error: unknown): boolean {
  const data = (error as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === 'string';
}

/** True when this Rudi release has no such practice-agents route. */
export function isMissingPracticeRoute(error: unknown, method: 'get' | 'write' = 'get'): boolean {
  if (isRudiError(error)) return false;
  return isMissingRoute(error) || (method === 'get' && statusOf(error) === 422);
}

function usePracticeAgentMutation<V, R>(
  request: (api: ReturnType<typeof useApiClient>, variables: V) => Promise<R>
) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variables: V) => request(api, variables),
    onSettled: () => qc.invalidateQueries({ queryKey: ['practice-agents'] }),
  });
}

/** Null when this Rudi release has no practice computers. */
export function usePracticeAgents(includeRevoked: boolean) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'devices', includeRevoked],
    queryFn: async () => {
      try {
        return (
          await api.get<{ items: PracticeAgentDevice[] }>(PRACTICE_AGENTS, {
            params: includeRevoked ? { includeRevoked: true } : {},
          })
        ).data.items;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
    refetchInterval: 60_000,
  });
}

export function usePracticeAgent(deviceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'device', deviceId],
    queryFn: async () =>
      (await api.get<PracticeAgentDeviceDetail>(`${PRACTICE_AGENTS}/${deviceId}`)).data,
    enabled: !!deviceId,
    retry: false,
    refetchInterval: 60_000,
  });
}

export function usePracticeAgentJobs(deviceId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'jobs', deviceId],
    queryFn: async () =>
      (await api.get<{ items: PracticeAgentJob[] }>(`${PRACTICE_AGENTS}/${deviceId}/jobs`)).data
        .items,
    enabled: !!deviceId,
    retry: false,
    refetchInterval: 15_000,
  });
}

export function useRevokePracticeAgent() {
  return usePracticeAgentMutation(
    async (api, deviceId: string) =>
      (await api.post<PracticeAgentDeviceDetail>(`${PRACTICE_AGENTS}/${deviceId}/revoke`)).data
  );
}

export function useSetPracticeAgentChannel() {
  return usePracticeAgentMutation(
    async (api, { deviceId, channel }: { deviceId: string; channel: string }) =>
      (
        await api.put<PracticeAgentDeviceDetail>(`${PRACTICE_AGENTS}/${deviceId}/channel`, {
          channel,
        })
      ).data
  );
}

export function useSetPracticeAgentBundlePin() {
  return usePracticeAgentMutation(
    async (api, { deviceId, version }: { deviceId: string; version: string | null }) =>
      (
        await api.put<PracticeAgentDeviceDetail>(`${PRACTICE_AGENTS}/${deviceId}/bundle-pin`, {
          version,
        })
      ).data
  );
}

export function useQueuePracticeAgentJob() {
  return usePracticeAgentMutation(
    async (api, { deviceId, job }: { deviceId: string; job: PracticeAgentJobCreate }) =>
      (await api.post<PracticeAgentJob>(`${PRACTICE_AGENTS}/${deviceId}/jobs`, job)).data
  );
}

export function useCancelPracticeAgentJob() {
  return usePracticeAgentMutation(
    async (api, jobId: string) =>
      (await api.post<PracticeAgentJob>(`${PRACTICE_AGENTS}/jobs/${jobId}/cancel`)).data
  );
}

/** Null when this Rudi release has no app releases. */
export function usePracticeAgentReleases() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'releases'],
    queryFn: async () => {
      try {
        return (await api.get<{ items: PracticeAgentRelease[] }>(`${PRACTICE_AGENTS}/releases`))
          .data.items;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
}

/** Null when this Rudi release has no engine bundles. */
export function usePracticeAgentBundles() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'bundles'],
    queryFn: async () => {
      try {
        return (await api.get<{ items: PracticeAgentBundle[] }>(`${PRACTICE_AGENTS}/bundles`)).data
          .items;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
  });
}

export function useSetBundleChannels() {
  return usePracticeAgentMutation(
    async (api, { version, channels }: { version: string; channels: string[] }) =>
      (
        await api.put<PracticeAgentBundle>(
          `${PRACTICE_AGENTS}/bundles/${encodeURIComponent(version)}/channels`,
          { channels }
        )
      ).data
  );
}

export function useYankBundle() {
  return usePracticeAgentMutation(
    async (api, version: string) =>
      (
        await api.post<PracticeAgentBundle>(
          `${PRACTICE_AGENTS}/bundles/${encodeURIComponent(version)}/yank`
        )
      ).data
  );
}

/**
 * A Practice's setup links. On a Rudi without setup links this reads the
 * setup codes instead, and `source` says which.
 */
export function usePracticeSetupLinks(practiceId: string, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'setup-links', practiceId],
    queryFn: async (): Promise<PracticeSetupLinks> => {
      const params = { practiceId };
      try {
        const links = await api.get<{ items: PracticeAgentSetupLink[] }>(
          `${PRACTICE_AGENTS}/setup-links`,
          { params }
        );
        return { source: 'links', items: links.data.items };
      } catch (error) {
        if (!isMissingPracticeRoute(error)) throw error;
      }
      const codes = await api.get<{ items: PracticeAgentSetupCode[] }>(
        `${PRACTICE_AGENTS}/setup-codes`,
        { params }
      );
      return { source: 'codes', items: codes.data.items };
    },
    enabled: enabled && !!practiceId,
    retry: false,
  });
}

/**
 * Create a setup link, which Rudi emails with an invitation. A Rudi without
 * setup links issues a bare setup code instead and sends nothing.
 */
export function useCreatePracticeSetupLink() {
  return usePracticeAgentMutation(
    async (api, body: PracticeAgentSetupLinkCreate): Promise<PracticeIssuedSetup> => {
      try {
        const link = (
          await api.post<PracticeAgentIssuedSetupLink>(`${PRACTICE_AGENTS}/setup-links`, body)
        ).data;
        return {
          source: 'links',
          code: link.code,
          url: link.url,
          expiresAt: link.expiresAt,
          deliveryStatus: link.deliveryStatus ?? null,
          email: link.email ?? body.email,
          invitation: link.invitation?.status ?? null,
        };
      } catch (error) {
        if (!isMissingPracticeRoute(error, 'write')) throw error;
      }
      const issued = (
        await api.post<PracticeAgentIssuedSetupCode>(`${PRACTICE_AGENTS}/setup-codes`, {
          practiceId: body.practiceId,
        })
      ).data;
      return {
        source: 'codes',
        code: issued.code,
        url: null,
        expiresAt: issued.expiresAt,
        deliveryStatus: null,
        email: null,
        invitation: null,
      };
    }
  );
}

export function useRevokePracticeSetupLink() {
  return usePracticeAgentMutation(
    async (api, { id, source }: { id: string; source: PracticeSetupSource }) => {
      await api.delete(
        `${PRACTICE_AGENTS}/${source === 'links' ? 'setup-links' : 'setup-codes'}/${id}`
      );
    }
  );
}

/** Null when this Rudi release does not report overdue exports. */
export function useOverduePractices() {
  const api = useApiClient();
  return useQuery({
    queryKey: ['practice-agents', 'overdue'],
    queryFn: async () => {
      try {
        return (await api.get<{ items: OverduePractice[] }>(`${PRACTICE_AGENTS}/overdue`)).data
          .items;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    retry: false,
    refetchInterval: 5 * 60_000,
  });
}

// ── Pre-screening ───────────────────────────────────────────────────
// Rudi's `/cortex/prescreening` routes. A Rudi without them answers 404 with
// FastAPI's `detail`; Rudi's own 404 for a missing run or query carries
// `error`, so `isMissingPracticeRoute` tells the two apart.

const PRESCREENING = '/cortex/prescreening';

function usePrescreeningMutation<V, R>(
  request: (api: ReturnType<typeof useApiClient>, variables: V) => Promise<R>
) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variables: V) => request(api, variables),
    onSettled: () => qc.invalidateQueries({ queryKey: ['prescreening'] }),
  });
}

export interface PrescreeningRunFilters {
  practiceId?: string;
  queryId?: string;
  /** true for draft runs only, false for approved versions only. */
  draft?: boolean;
}

/** Newest first. Null when this Rudi release has no pre-screening. */
export function usePrescreeningRuns(filters: PrescreeningRunFilters = {}, enabled = true) {
  const api = useApiClient();
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined)
  );
  return useQuery({
    queryKey: ['prescreening', 'runs', params],
    queryFn: async () => {
      try {
        return (
          await api.get<{ items: PrescreeningRunSummary[] }>(`${PRESCREENING}/runs`, { params })
        ).data.items;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
}

export function usePrescreeningRun(runId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['prescreening', 'run', runId],
    queryFn: async () =>
      (await api.get<PrescreeningRunDetail>(`${PRESCREENING}/runs/${runId}`)).data,
    enabled: !!runId,
    retry: false,
  });
}

export interface PrescreeningQueryFilters {
  practiceId?: string;
  status?: PrescreeningQueryStatus;
}

/** Newest change first. Null when this Rudi release has no pre-screening. */
export function usePrescreeningQueries(filters: PrescreeningQueryFilters = {}, enabled = true) {
  const api = useApiClient();
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined)
  );
  return useQuery({
    queryKey: ['prescreening', 'queries', params],
    queryFn: async () => {
      try {
        return (
          await api.get<{ items: PrescreeningQuery[] }>(`${PRESCREENING}/queries`, { params })
        ).data.items;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    enabled,
    retry: false,
    refetchInterval: 60_000,
  });
}

export function usePrescreeningQuery(queryId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['prescreening', 'query', queryId],
    queryFn: async () =>
      (await api.get<PrescreeningQuery>(`${PRESCREENING}/queries/${queryId}`)).data,
    enabled: !!queryId,
    retry: false,
    refetchInterval: 60_000,
  });
}

export function useApprovePrescreeningVersion() {
  return usePrescreeningMutation(
    async (api, { queryId, version }: { queryId: string; version: number }) =>
      (
        await api.post<PrescreeningQuery>(
          `${PRESCREENING}/queries/${queryId}/versions/${version}/approve`
        )
      ).data
  );
}

/** An operator's version. It stops the designer for this query. */
export function useCreatePrescreeningVersion() {
  return usePrescreeningMutation(
    async (api, { queryId, body }: { queryId: string; body: PrescreeningVersionCreate }) =>
      (await api.post<PrescreeningQuery>(`${PRESCREENING}/queries/${queryId}/versions`, body)).data
  );
}

/** Null when this Rudi release has no code sets. */
export function usePracticeCodeSets(practiceId: string, enabled = true) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['prescreening', 'code-sets', practiceId],
    queryFn: async () => {
      try {
        return (
          await api.get<PracticeCodeSets>(`${PRESCREENING}/practices/${practiceId}/code-sets`)
        ).data;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    enabled: enabled && !!practiceId,
    retry: false,
  });
}

export function useSavePracticeCodeSets() {
  return usePrescreeningMutation(
    async (
      api,
      { practiceId, codeSets }: { practiceId: string; codeSets: Record<string, string[]> }
    ) =>
      (
        await api.put<PracticeCodeSets>(`${PRESCREENING}/practices/${practiceId}/code-sets`, {
          codeSets,
        })
      ).data
  );
}

/** Null when this Rudi release has no patient model settings. */
export function useTenantPatientModel(tenantId: string) {
  const api = useApiClient();
  return useQuery({
    queryKey: ['prescreening', 'patient-model', tenantId],
    queryFn: async () => {
      try {
        return (
          await api.get<PatientModelSettings>(`${PRESCREENING}/tenants/${tenantId}/patient-model`)
        ).data;
      } catch (error) {
        if (isMissingPracticeRoute(error)) return null;
        throw error;
      }
    },
    enabled: !!tenantId,
    retry: false,
  });
}

export function useSaveTenantPatientModel() {
  return usePrescreeningMutation(
    async (api, { tenantId, body }: { tenantId: string; body: PatientModelSettingsUpdate }) =>
      (
        await api.put<PatientModelSettings>(
          `${PRESCREENING}/tenants/${tenantId}/patient-model`,
          body
        )
      ).data
  );
}

/** Store a new key for the practice computer. Rudi revokes the one before it. */
export function useSetPatientModelKey() {
  return usePrescreeningMutation(
    async (api, { deviceId, apiKey }: { deviceId: string; apiKey: string }) =>
      (
        await api.put<PatientModelKey>(`${PRESCREENING}/devices/${deviceId}/patient-model-key`, {
          apiKey,
        })
      ).data
  );
}

export function useRevokePatientModelKey() {
  return usePrescreeningMutation(async (api, deviceId: string) => {
    await api.delete(`${PRESCREENING}/devices/${deviceId}/patient-model-key`);
  });
}
